/**
 * PartnerHub ref_code helpers.
 *
 * The landing page at create-products.com sets a cookie `ph_ref` on the parent
 * domain `.create-products.com` (shared with this SPA on app.create-products.com).
 * That cookie is the cross-subdomain bridge: it survives even when the user
 * bypasses the landing CTA (bookmark, logged-in session in another tab, etc.),
 * whereas localStorage is scoped strictly per origin and can't cross subdomains.
 *
 * Priority order when reading:
 *   1. localStorage.ph_ref (already captured this session, most specific)
 *   2. Cookie `ph_ref` on .create-products.com (fallback for bypass scenarios)
 */

export function readPhRefFromCookie(): string {
  if (typeof document === 'undefined' || !document.cookie) return '';
  const match = document.cookie.match(/(?:^|;\s*)ph_ref=([^;]+)/);
  const value = match && match[1];
  return value ? decodeURIComponent(value) : '';
}

/**
 * Idempotent: if localStorage.ph_ref is empty, populate it from the shared
 * cookie. Call once on SPA bootstrap. Safe to call multiple times.
 * Silent-fails on storage APIs being blocked (private mode, etc.).
 */
export function hydratePhRef(): void {
  try {
    if (localStorage.getItem('ph_ref')) return;
    const fromCookie = readPhRefFromCookie();
    if (fromCookie) localStorage.setItem('ph_ref', fromCookie);
  } catch {
    // storage disabled — give up quietly
  }
}

/**
 * Read ph_ref with fallback: localStorage first, then cookie.
 * Returns '' if neither has it. Use this before sending payment requests.
 */
export function getPhRef(): string {
  try {
    const fromLocal = localStorage.getItem('ph_ref');
    if (fromLocal) return fromLocal;
  } catch { /* noop */ }
  return readPhRefFromCookie();
}
